import { defineMessages } from 'react-intl';
import { intl } from 'hellospa/components/hs-intl-provider';
import { AutofillFieldTypes } from './types';

export type Features = { [key: string]: boolean }

export const ZERO_WIDTH_SPACE = '\u{200b}';

// User visible text to show when editing a mixed value. If you select multiple
// fields assigned to different people, this is what the dropdown will default
// to.
export const MIXED_TEXT = '----';

export const FEATURE_TEMPLATE_ROLES = { FEATURE_TEMPLATE_ROLES: true };
export const FEATURE_CAN_ADD_SIGNER = { FEATURE_CAN_ADD_SIGNER: true };
export const FEATURE_EDIT_SIGNERS = { FEATURE_EDIT_SIGNERS: true };
export const FEATURE_EDIT_DOCUMENTS = { FEATURE_EDIT_DOCUMENTS: true };
export const FEATURE_SENDER_ASSIGN = { FEATURE_SENDER_ASSIGN: true };
export const FEATURE_EMBED = { FEATURE_EMBED: true };

export const FEATURE_DROPDOWN = { FEATURE_DROPDOWN: true };
export const FEATURE_RADIO = { FEATURE_RADIO: true };
export const FEATURE_CONDITIONAL_LOGIC = { FEATURE_CONDITIONAL_LOGIC: true };
export const FEATURE_CONDITIONAL_LOGIC_NESTED = { FEATURE_CONDITIONAL_LOGIC_NESTED: true };
export const FEATURE_FONT_COLOR = { FEATURE_FONT_COLOR: true };
export const FEATURE_SIGNATURE_COLOR = { FEATURE_SIGNATURE_COLOR: true };
export const FEATURE_HELP_TEXT = { FEATURE_HELP_TEXT: true };
export const FEATURE_ROTATE_PAGES = { FEATURE_ROTATE_PAGES: true };
export const FEATURE_DATE_FORMAT = { FEATURE_DATE_FORMAT: true };

export const FEATURE_HYPERLINKS = { FEATURE_HYPERLINKS: true };
export const FEATURE_MASKED_FIELDS = { FEATURE_MASKED_FIELDS: true };

export const FEATURE_TEMPLATE_ONE_OFF_FILE = { FEATURE_TEMPLATE_ONE_OFF_FILE: true };

export const FEATURE_AUTOFILL_FIELDS = { FEATURE_AUTOFILL_FIELDS: true };
export const FEATURE_PREVIEW = { FEATURE_PREVIEW: true };
export const FEATURE_PREVIEW_ONLY = { FEATURE_PREVIEW_ONLY: true };

export const FEATURE_EDITOR_MOBILE = { FEATURE_EDITOR_MOBILE: true };

export const FEATURE_QES = { FEATURE_QES: true };

export const FEATURE_DOC_ACTIONS_ADD = { FEATURE_DOC_ACTIONS_ADD: true };
export const FEATURE_DOC_ACTIONS_ROTATE = { FEATURE_DOC_ACTIONS_ROTATE: true };
export const FEATURE_DOC_ACTIONS_REPLACE = { FEATURE_DOC_ACTIONS_REPLACE: true };
export const FEATURE_DOC_ACTIONS_REORDER = { FEATURE_DOC_ACTIONS_REORDER: true };

export const FEATURE_FIELDS_PANEL_TOUR = { FEATURE_FIELDS_PANEL_TOUR: true };

export const FEATURE_ADVANCED_MERGE_FIELD = { FEATURE_ADVANCED_MERGE_FIELD: true };

export function addFeature(mode: Features, feature: Features) {
  return { ...mode, ...feature };
}

export const FEATURE_EDITOR_VARIANT_1 = addFeature(
  // Anything that should apply to all mobile variants should check the mobile
  // flag. For example, you can't drag to select on mobile.
  FEATURE_EDITOR_MOBILE,
  { FEATURE_EDITOR_VARIANT_1: true },
);

export function hasFeature(mode: Features, feature: Features): boolean {

  // Verify that ALL items of the feature are present in the mode, this allows
  // for checking groups of features. `hasFeature(mode, MODE_EDIT_TEMPLATE)`
  let allFeaturesAreInMode = true;
  Object.getOwnPropertyNames(feature).forEach((propertyKey) => {
    /* eslint-disable no-prototype-builtins */
    if (mode && !mode.hasOwnProperty(propertyKey)) {
      allFeaturesAreInMode = false;
    }
    /* eslint-enable no-prototype-builtins */
  });

  return mode && allFeaturesAreInMode && Object.getOwnPropertyNames(feature).length > 0;
}

export function removeFeature(mode: Features, feature: Features) {
  const modeWithoutFeature = { ...mode };
  Object.getOwnPropertyNames(feature).forEach((propertyKey) => {
    delete modeWithoutFeature[propertyKey];
  });

  return modeWithoutFeature;
}

// This is used for keyboard shortcuts
export const IS_MAC = Boolean(navigator.platform.match(/(Mac|iPhone|iPod|iPad)/i));

type DefaultDimensions = {
  [key: string]: {
    width?: number,
    height?: number,
  }
}
const fieldsMinDimensions: DefaultDimensions = {
  signature: {
    width: 70,
  },
  initials: {
    width: 40,
  },
  date: {
    width: 75,
  },
  text: {
    // The minimum size for text fields are handled in CSS so they can account
    // for font size.
    // src/hellospa/components/signature-request/display-field/text.scss
  },
};
export function getMinFieldDimensions(fieldType: string, fieldDimension: 'width' | 'height'): number {
  if (
    fieldsMinDimensions[fieldType] == null
    || fieldsMinDimensions[fieldType][fieldDimension] == null
  ) {
    return 8;
  }

  return fieldsMinDimensions[fieldType][fieldDimension]!;
}

export const fontSizes = [7, 8, 9, 10, 12, 14, 16, 18, 20, 24, 26, 28, 36, 42, 48];

export const legacyFontSizes = [8, 10, 12, 14, 16, 18, 20, 22];

// <const> for typehinting value exists in array
// see http://dbx.link/ts-const-assertions
export const signatureColors = <const> [
  'black',
  'blue',
  'red',
];

export const fontColors = <const> [
  'black',
  'blue',
  'red',
  'white',
];

export const fontFamilies = [
  'helvetica',
  'arial',
  'calibri',
  'cambria',
  'courier',
  'georgia',
  'times',
  'trebuchet',
  'verdana',
  'notoSerif',
  'notoSans',
  'roboto',
  'robotoMono',
] as const;

const dateFormatsMessages = defineMessages({
  MMDDYYYYFS: {
    id: '',
    description: 'date format for user to select in editor',
    defaultMessage: 'MM / DD / YYYY',
  },
  MMDDYYYYH: {
    id: '',
    description: 'date format for user to select in editor',
    defaultMessage: 'MM - DD - YYYY',
  },
  DDMMYYYYFS: {
    id: '',
    description: 'date format for user to select in editor',
    defaultMessage: 'DD / MM / YYYY',
  },
  DDMMYYYYH: {
    id: '',
    description: 'date format for user to select in editor',
    defaultMessage: 'DD - MM - YYYY',
  },
  YYYYMMDDFS: {
    id: '',
    description: 'date format for user to select in editor',
    defaultMessage: 'YYYY / MM / DD',
  },
  YYYYMMDDH: {
    id: '',
    description: 'date format for user to select in editor',
    defaultMessage: 'YYYY - MM - DD',
  },
});

export const dateFormats = {
  default: 'Default',
  'MM / DD / YYYY': intl.formatMessage(dateFormatsMessages.MMDDYYYYFS),
  'MM - DD - YYYY': intl.formatMessage(dateFormatsMessages.MMDDYYYYH),
  'DD / MM / YYYY': intl.formatMessage(dateFormatsMessages.DDMMYYYYFS),
  'DD - MM - YYYY': intl.formatMessage(dateFormatsMessages.DDMMYYYYH),
  'YYYY / MM / DD': intl.formatMessage(dateFormatsMessages.YYYYMMDDFS),
  'YYYY - MM - DD': intl.formatMessage(dateFormatsMessages.YYYYMMDDH),

  // future date formats to support
  // 'DD MMMM YYYY': 'DD Month YYYY',
  // 'MMM. DD, YYYY': 'Mon. DD, YYYY',
  // 'MMMM DD, YYYY': 'Month DD, YYYY',
};

const validationMessages = defineMessages({
  optionNumbersOnly: {
    id: '',
    description: 'A field validation option name requiring that the user enter only numbers.',
    defaultMessage: 'Numbers only',
  },
  optionLettersOnly: {
    id: '',
    description: 'A field validation option name requiring that the user enter only letters.',
    defaultMessage: 'Letters only',
  },
  optionPhoneNumber: {
    id: '',
    description: 'A field validation option name requiring that the user enter only a valid phone number.',
    defaultMessage: '(USA) Phone number',
  },
  optionBankRoutingNumber: {
    id: '',
    description: 'A field validation option name requiring that the user enter only a valid bank routing number.',
    defaultMessage: '(USA) Bank routing number',
  },
  optionBankAccountNumber: {
    id: '',
    description: 'A field validation option name requiring that the user enter only a valid bank account number.',
    defaultMessage: '(USA) Bank account number',
  },
  optionEmailAddress: {
    id: '',
    description: 'A field validation option name requiring that the user enter only a valid email address.',
    defaultMessage: 'Email address',
  },
  optionZipCode: {
    id: '',
    description: 'A field validation option name requiring that the user enter only a valid zip code.',
    defaultMessage: '(USA) Zip code',
  },
  optionSSN: {
    id: '',
    description: 'A field validation option name requiring that the user enter only a valid social security number.',
    defaultMessage: '(USA) Social security number',
  },
  optionEIN: {
    id: '',
    description: 'A field validation option name requiring that the user enter only a valid employer identification number.',
    defaultMessage: '(USA) Employer identification number',
  },
  optionCustomRegex: {
    id: '',
    description: 'A field validation option name which validates the user\'s input using a custom regular expression.',
    defaultMessage: 'Custom regex',
  },
  detailPhoneNumber: {
    id: '',
    description: 'The description of a field validation option which requires the to user enter a valid phone number.',
    defaultMessage: '10 digits will be required',
  },
  detailBankRoutingNumber: {
    id: '',
    description: 'The description of a field validation option which requires the to user enter a bank routing number.',
    defaultMessage: '9 digits will be required',
  },
  detailBankAccountNumber: {
    id: '',
    description: 'The description of a field validation option which requires the to user enter a bank account number.',
    defaultMessage: 'Minimum of 6 digits will be required',
  },
  detailZipCode: {
    id: '',
    description: 'The description of a field validation option which requires the to user enter a valid zip ode.',
    defaultMessage: '5 or 9 digits will be required',
  },
  detailSSN: {
    id: '',
    description: 'The description of a field validation option which requires the to user enter a valid social security number.',
    defaultMessage: '9 digits will be required',
  },
  detailEIN: {
    id: '',
    description: 'The description of a field validation option which requires the to user enter a valid employer identification number.',
    defaultMessage: '9 digits will be required',
  },
  detailCustomRegex: {
    id: '',
    description: 'The description of a field validation option which validates the user\'s input using a custom regular expression.',
    defaultMessage: 'Specify a regular expression',
  },
});

const autofillFieldMessages = defineMessages({
  companyFieldLabel: {
    id: '',
    description: 'Text describing a type of field a user can drag into the document. In this case, a "Company" field.',
    defaultMessage: 'Company',
  },
  emailFieldLabel: {
    id: '',
    description: 'Text describing a type of field a user can drag into the document. In this case, a "Email" field.',
    defaultMessage: 'Email',
  },
  fullNameFieldLabel: {
    id: '',
    description: 'Text describing a type of field a user can drag into the document. In this case, a "Full Name" field.',
    defaultMessage: 'Full Name',
  },
  lastNameFieldLabel: {
    id: '',
    description: 'Text describing a type of field a user can drag into the document. In this case, a "Last Name" field.',
    defaultMessage: 'Last Name',
  },
  firstNameFieldLabel: {
    id: '',
    description: 'Text describing a type of field a user can drag into the document. In this case, a "First Name" field.',
    defaultMessage: 'First Name',
  },
  titleFieldLabel: {
    id: '',
    description: 'Text describing a type of field a user can drag into the document. In this case, a "Title" field.',
    defaultMessage: 'Title',
  },
});

export const validationOptions = {
  numbers_only: intl.formatMessage(validationMessages.optionNumbersOnly),
  letters_only: intl.formatMessage(validationMessages.optionLettersOnly),
  phone_number: intl.formatMessage(validationMessages.optionPhoneNumber),
  bank_routing_number: intl.formatMessage(validationMessages.optionBankRoutingNumber),
  bank_account_number: intl.formatMessage(validationMessages.optionBankAccountNumber),
  email_address: intl.formatMessage(validationMessages.optionEmailAddress),
  zip_code: intl.formatMessage(validationMessages.optionZipCode),
  social_security_number: intl.formatMessage(validationMessages.optionSSN),
  employer_identification_number: intl.formatMessage(validationMessages.optionEIN),
  custom_regex: intl.formatMessage(validationMessages.optionCustomRegex),
};

export const validationOptionsDetail = {
  phone_number: intl.formatMessage(validationMessages.detailPhoneNumber),
  bank_routing_number: intl.formatMessage(validationMessages.detailBankRoutingNumber),
  bank_account_number: intl.formatMessage(validationMessages.detailBankAccountNumber),
  zip_code: intl.formatMessage(validationMessages.detailZipCode),
  social_security_number: intl.formatMessage(validationMessages.detailSSN),
  employer_identification_number: intl.formatMessage(validationMessages.detailEIN),
  custom_regex: intl.formatMessage(validationMessages.detailCustomRegex),
};

export const autofillFieldsTitle: Record<AutofillFieldTypes, string> = {
  name: intl.formatMessage(autofillFieldMessages.fullNameFieldLabel),
  firstName: intl.formatMessage(autofillFieldMessages.firstNameFieldLabel),
  lastName: intl.formatMessage(autofillFieldMessages.lastNameFieldLabel),
  company: intl.formatMessage(autofillFieldMessages.companyFieldLabel),
  email: intl.formatMessage(autofillFieldMessages.emailFieldLabel),
  title: intl.formatMessage(autofillFieldMessages.titleFieldLabel),
  none: '',
};

export const featureMap = {
  dropdown: FEATURE_DROPDOWN,
  radiobutton: FEATURE_RADIO,
  rotate: FEATURE_ROTATE_PAGES,
  editSigners: FEATURE_EDIT_SIGNERS,
  editDocuments: FEATURE_EDIT_DOCUMENTS,
  docActionsAdd: FEATURE_DOC_ACTIONS_ADD,
  docActionsRotate: FEATURE_DOC_ACTIONS_ROTATE,
  docActionsReplace: FEATURE_DOC_ACTIONS_REPLACE,
  docActionsReorder: FEATURE_DOC_ACTIONS_REORDER,
  conditionalLogic: FEATURE_CONDITIONAL_LOGIC,
  // I'm not sure if we really want this to be a flag, but I want to be able to
  // easily find the code that disables nested rules when we want to turn it on.
  conditionalLogicNested: FEATURE_CONDITIONAL_LOGIC_NESTED,
  hyperlinks: FEATURE_HYPERLINKS,
  maskedFields: FEATURE_MASKED_FIELDS,
  dateFormat: FEATURE_DATE_FORMAT,
  autoFillFields: FEATURE_AUTOFILL_FIELDS,
  qualifiedSignature: FEATURE_QES,

  editorVariant1: FEATURE_EDITOR_VARIANT_1,

  /**
   * These feature are not specific to sending or editing templates.
   */
  templatePreview: FEATURE_PREVIEW,
  templatePreviewOnly: FEATURE_PREVIEW_ONLY,

  /**
   * This feature flag for enabling a tour for the editor
   */
  fieldsPanelTour: FEATURE_FIELDS_PANEL_TOUR,

  /**
   * This feature for enabling advanced merge field panel for integration context
   */
  advancedMergeField: FEATURE_ADVANCED_MERGE_FIELD,
};

// https://github.com/HelloFax/HelloFax/blob/develop/lib/form/doctrine/TransmissionGroupForm.class.php#L17-L28
export enum PnsRequestTypes {
  SendDoc = 1, // Document fax request & Just me (sign & send)
  RequestSig = 2, // Non-template document signature request
  ResponseSig = 3, // Signer flow
  Template = 4, // Template creation
  SendTemplate = 5, // Signature request with a template
  Demo = 7, // "See how it works" flow
  FinalizeDocGmail = 8, // Gmail integration?
  ReusableLink = 9, // Template link creation request
  SelfSave = 10, // Just me (sign & save) flow
  EmbeddedRequest = 11, // Embedded non-template signature request
  EmbeddedTemplate = 12, // Embedded template signature request
  BulkSend = 13, // Bulk send signature request
}

// Radio button pairs are created 24-units apart
export const RADIO_SPACING = 24;

// track heap editor signer and assigned to dropdown
export const ASSIGNED_TO_LEFT = 'Assigned to left';
export const ASSIGNED_TO_RIGHT = 'Assigned to RIGHT';

export const MAX_FILE_SIZE = 40 * 1000 * 1000; // 40 MB
export const MAX_POLL_COUNT = 300;
